import CryptoJS from 'crypto-js';
import forge from 'node-forge';
import { getPublicKey } from './certificate';

/**
 * Envelope encryption scheme (matches the Fibe mobile app and the partners web app).
 *
 * Every request gets a fresh random AES-256 session key. The body is encrypted
 * with it, and the session key itself is RSA-OAEP encrypted under the server's
 * public certificate and sent in the `uuid_wp` header. The server unwraps the
 * key, decrypts the body, and encrypts its response with the same session key.
 *
 * The request/response formats are deliberately asymmetric -- do not "fix" them:
 *
 *   Request  -> IV is 16 zero bytes, and 16 zero bytes are PREPENDED to the
 *               plaintext before encrypting. The server strips them and uses
 *               their presence to detect the new scheme.
 *   Response -> the server generates a random IV and PREPENDS it to the
 *               ciphertext. No zero prefix on the plaintext.
 */

const SESSION_KEY_LENGTH = 32; // 32 ASCII chars -> 32 UTF-8 bytes -> AES-256
const SESSION_KEY_CHARSET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const BLOCK_SIZE_BYTES = 16;
const BLOCK_SIZE_WORDS = BLOCK_SIZE_BYTES / 4; // CryptoJS WordArrays hold 4-byte words
const ZERO_BLOCK_HEX = '00'.repeat(BLOCK_SIZE_BYTES);

// 256 is not a multiple of the 62-char charset, so bytes are drawn until they
// fall inside the largest usable multiple. This keeps the key uniform rather
// than biasing the first 8 characters of the charset.
const LARGEST_UNBIASED_BYTE =
  Math.floor(256 / SESSION_KEY_CHARSET.length) * SESSION_KEY_CHARSET.length;

const getRandomValues = (byteCount) => {
  const cryptoObject =
    (typeof globalThis !== 'undefined' && globalThis.crypto) ||
    (typeof window !== 'undefined' && window.crypto);

  if (!cryptoObject || typeof cryptoObject.getRandomValues !== 'function') {
    throw new Error('[SECURE] No cryptographically secure random source');
  }

  const bytes = new Uint8Array(byteCount);
  cryptoObject.getRandomValues(bytes);
  return bytes;
};

/**
 * Generates a fresh 32-character AES session key. Never uses `Math.random()`.
 * @returns {string} 32 characters from [A-Za-z0-9]
 * UUID
 */
export const generateSessionKey = () => {
  let sessionKey = '';

  while (sessionKey.length < SESSION_KEY_LENGTH) {
    const bytes = getRandomValues(SESSION_KEY_LENGTH);

    for (
      let i = 0;
      i < bytes.length && sessionKey.length < SESSION_KEY_LENGTH;
      i += 1
    ) {
      if (bytes[i] < LARGEST_UNBIASED_BYTE) {
        sessionKey +=
          SESSION_KEY_CHARSET[bytes[i] % SESSION_KEY_CHARSET.length];
      }
    }
  }

  return sessionKey;
};

/**
 * Encrypts a request payload with the session key.
 * @param {string} sessionKey - 32-character session key
 * @param {*} objectToEncrypt - Object to serialize and encrypt
 * @returns {string} Base64 ciphertext for the `data` field
 */
export const encryptEnvelope = (sessionKey, objectToEncrypt) => {
  const key = CryptoJS.enc.Utf8.parse(sessionKey);
  const zeroInitVector = CryptoJS.enc.Hex.parse(ZERO_BLOCK_HEX);

  // 16 zero bytes + JSON. `concat` mutates its receiver, which is a freshly
  // parsed WordArray here, so nothing shared is modified.
  const plainText = CryptoJS.enc.Hex.parse(ZERO_BLOCK_HEX).concat(
    CryptoJS.enc.Utf8.parse(JSON.stringify(objectToEncrypt))
  );

  return CryptoJS.AES.encrypt(plainText, key, {
    iv: zeroInitVector,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).ciphertext.toString(CryptoJS.enc.Base64);
};

/**
 * Decrypts a response encrypted with the same session key.
 * @param {string} sessionKey - The session key used for the request
 * @param {string} encryptedString - Base64 string from the `response` field
 * @returns {*} JSON parsed response object
 */
export const decryptEnvelope = (sessionKey, encryptedString) => {
  const key = CryptoJS.enc.Utf8.parse(sessionKey);
  const raw = CryptoJS.enc.Base64.parse(encryptedString);

  if (raw.sigBytes <= BLOCK_SIZE_BYTES) {
    throw new Error('[SECURE] Envelope response shorter than its IV');
  }

  // The server prepends the IV it used to the ciphertext.
  const initVector = CryptoJS.lib.WordArray.create(
    raw.words.slice(0, BLOCK_SIZE_WORDS),
    BLOCK_SIZE_BYTES
  );
  const cipherText = CryptoJS.lib.WordArray.create(
    raw.words.slice(BLOCK_SIZE_WORDS),
    raw.sigBytes - BLOCK_SIZE_BYTES
  );

  const decryptedString = CryptoJS.AES.decrypt(
    { ciphertext: cipherText },
    key,
    {
      iv: initVector,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  ).toString(CryptoJS.enc.Utf8);

  return JSON.parse(decryptedString);
};

/**
 * RSA-OAEP encrypts the session key for the `uuid_wp` request header.
 * @param {string} sessionKey - 32-character session key
 * @returns {string} Base64 RSA ciphertext (344 chars for a 2048-bit key)
 */
export const encryptSessionKey = (sessionKey) => {
  const encrypted = getPublicKey().encrypt(sessionKey, 'RSA-OAEP', {
    md: forge.md.sha1.create(),
    mgf1: { md: forge.md.sha1.create() },
  });

  return forge.util.encode64(encrypted);
};
